import type { CSSProperties } from "react";

import { TOption } from "../../../models/option.model";

export type TGeneratorPanelVariant = "default" | "colors" | "patterns";

function slugify(raw: string): string {
    return raw
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .replace(/[^a-z0-9]+/g, "");
}

function optionTokens(option: TOption): string[] {
    const parts = [option.label, String(option.value ?? ""), String((option as { id?: string | number }).id ?? "")];
    return parts.map((p) => slugify(String(p))).filter(Boolean);
}

/** Klucz do paska koloru (atrybut data-color / moduł SCSS). */
export function resolveGeneratorColorKey(option: TOption): string {
    const synonyms: Record<string, string> = {
        bialy: "bialy",
        white: "bialy",
        czarny: "czarny",
        black: "czarny",
        brazowy: "brazowy",
        brown: "brazowy",
        rozowy: "rozowy",
        pink: "rozowy",
        fioletowy: "fioletowy",
        purple: "fioletowy",
        zolty: "zolty",
        yellow: "zolty",
        zielony: "zielony",
        green: "zielony",
        niebieski: "niebieski",
        blue: "niebieski",
        czerwony: "czerwony",
        red: "czerwony",
        pomaranczowy: "pomaranczowy",
        orange: "pomaranczowy",
        bezowy: "bezowy",
        beige: "bezowy",
        szary: "szary",
        grey: "szary",
        gray: "szary",
        wielokolorowy: "wielokolorowy",
        multicolor: "wielokolorowy",
        srebrny: "srebrny",
        silver: "srebrny",
        zloty: "zloty",
        gold: "zloty",
    };

    for (const token of optionTokens(option)) {
        if (synonyms[token]) return synonyms[token];
        if (synonyms[token.replace(/y$/, "")]) return synonyms[token.replace(/y$/, "")];
    }
    return "neutral";
}

/** Tło paska koloru: priorytetowo z imgUrl, fallback do mapowania po etykiecie. */
export function getGeneratorColorStripStyle(option: TOption): CSSProperties {
    if (option.imgUrl) {
        return {
            backgroundImage: `url("${option.imgUrl}")`,
            // backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
        };
    }
    return {};
}

/** Tło kółka wzoru: URL z API lub prosty fallback CSS. */
export function getGeneratorPatternCircleStyle(option: TOption): CSSProperties {
    if (option.imgUrl) {
        return {
            backgroundImage: `url("${option.imgUrl}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
        };
    }

    const tokens = optionTokens(option);
    const has = (...keys: string[]) => keys.some((k) => tokens.some((t) => t.includes(k) || k.includes(t)));

    if (has("jednolity", "solid", "plain")) {
        return { background: "#d7b7dc" };
    }
    if (has("kwiatki", "floral", "flower")) {
        return {
            background: "repeating-linear-gradient(45deg, #e8b4d4 0 6px, #f5e6f0 6px 12px)",
        };
    }
    if (has("groszki", "polka", "dots")) {
        return {
            backgroundColor: "#f0e6f4",
            backgroundImage: "radial-gradient(#8a4bd6 22%, transparent 24%)",
            backgroundSize: "10px 10px",
        };
    }
    if (has("paski", "stripe")) {
        return {
            background: "repeating-linear-gradient(90deg, #6b4a7a 0 4px, #e9d6eb 4px 8px)",
        };
    }
    if (has("kratka", "plaid", "check")) {
        return {
            background:
                "linear-gradient(90deg, #c9a8cc 50%, transparent 50%), linear-gradient(#a878b0 50%, transparent 50%)",
            backgroundSize: "8px 8px",
        };
    }
    if (has("pepitka", "houndstooth")) {
        return {
            background: "conic-gradient(from 45deg, #444 0 25%, #ddd 0 50%, #444 0 75%, #ddd 0)",
            backgroundSize: "12px 12px",
        };
    }
    if (has("moro", "camo", "kamuflaz")) {
        return {
            background:
                "radial-gradient(circle at 20% 30%, #5a6b4e 0 25%, transparent 26%), radial-gradient(circle at 70% 60%, #3d4a38 0 20%, transparent 21%), #6b7558",
        };
    }
    if (has("zwierzec", "animal", "leopard")) {
        return {
            background:
                "radial-gradient(ellipse at 30% 40%, #c4a574 0 15%, transparent 16%), radial-gradient(ellipse at 70% 50%, #8b6914 0 12%, transparent 13%), #e8d4b8",
        };
    }
    if (has("napis", "typography", "text")) {
        return {
            background: "linear-gradient(135deg, #2a2a2a 0 40%, #f5f5f5 40% 100%)",
        };
    }
    if (has("logo")) {
        return {
            background: "linear-gradient(135deg, #333 50%, #bb93ae 50%)",
        };
    }

    return { background: "linear-gradient(135deg, #e9d6eb, #c9a8cc)" };
}
